import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { SalesService } from '../../../../shared/services/apis/platform/sales.service';
import { AdminSaleDetailsResponse, AdminSalesTrackingResponse } from '../../../../shared/services/responses/platform/admin-sales.response';
import { statusClass } from '../sales-status';
import { UtcDatePipe } from '../utc-date.pipe';
import { NfeUploadDialogComponent } from './nfe-upload-dialog.component';
import { LabelUploadDialogComponent } from './label-upload-dialog.component';
import { environment } from '../../../../../environments/environment';

export interface SaleDetailsDialogData {
  idKitPayment: number;
}

/** Detalhe completo da venda: linha do tempo, itens, cliente, pagamento (Asaas ao vivo), fiscal e entrega (rastreio ao vivo). */
@Component({
  selector: 'app-sale-details-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, UtcDatePipe],
  templateUrl: './sale-details-dialog.component.html',
  styleUrl: './sale-details-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaleDetailsDialogComponent implements OnInit {
  loading = true;
  error = '';
  d: AdminSaleDetailsResponse | null = null;
  tracking: AdminSalesTrackingResponse | null = null;
  trackingLoading = false;
  trackingError = '';
  /** true quando algo foi alterado (uploads): a lista deve recarregar ao fechar. */
  changed = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SaleDetailsDialogData,
    private dialogRef: MatDialogRef<SaleDetailsDialogComponent, boolean>,
    private dialog: MatDialog,
    private sales: SalesService,
    private alert: AlertService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void { this.load(); }

  statusClass = statusClass;

  get pct(): number {
    if (!this.d) return 0;
    const steps = this.d.timeline.filter(s => s.applies);
    const done = steps.filter(s => !!s.datetime).length;
    return steps.length ? Math.round((done / steps.length) * 100) : 0;
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.sales.details(this.data.idKitPayment).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.isError) { this.error = res.errorDescription || 'Falha ao carregar a venda.'; this.cdr.markForCheck(); return; }
        this.d = res;
        this.cdr.markForCheck();
        if (res.delivery?.coreiosTrackingCode && res.sale.hasPhisicalBook && res.delivery.sendDateTime) this.loadTracking(false);
      },
      error: (err) => { this.loading = false; this.error = err?.message || 'Erro inesperado.'; this.cdr.markForCheck(); },
    });
  }

  loadTracking(refresh: boolean): void {
    if (!this.d?.delivery?.coreiosTrackingCode) return;
    this.trackingLoading = true;
    this.trackingError = '';
    this.cdr.markForCheck();
    this.sales.tracking(this.d.sale.idKitPayment, refresh).subscribe({
      next: (res) => {
        this.trackingLoading = false;
        if (res.isError) { this.tracking = null; this.trackingError = res.errorDescription || 'Falha ao consultar os Correios.'; }
        else this.tracking = res;
        this.cdr.markForCheck();
      },
      error: (err) => { this.trackingLoading = false; this.trackingError = err?.message || 'Erro inesperado.'; this.cdr.markForCheck(); },
    });
  }

  openNfe(): void {
    if (!this.d) return;
    this.dialog.open(NfeUploadDialogComponent, {
      width: '720px', maxWidth: '95vw',
      data: { idKitPayment: this.d.sale.idKitPayment, hasXml: !!this.d.fiscal.linkXml, hasDanfe: !!this.d.fiscal.linkDanfe },
    }).afterClosed().subscribe(res => { if (res) { this.changed = true; this.load(); } });
  }

  openLabel(): void {
    if (!this.d) return;
    if (!this.d.fiscal.nfeNumber) { this.alert.show('Atenção', 'Anexe a NF-e (XML e DANFE) antes da etiqueta.', AlertType.warning); return; }
    this.dialog.open(LabelUploadDialogComponent, { width: '800px', maxWidth: '95vw', data: { details: this.d } })
      .afterClosed().subscribe(ok => { if (ok) { this.changed = true; this.load(); } });
  }

  open(url?: string | null): void {
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  }

  copy(text?: string | null): void {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(
      () => this.alert.show('Copiado', text, AlertType.info),
      () => this.alert.show('Atenção', 'Não foi possível copiar.', AlertType.warning));
  }

  /** Primeira etapa aplicável ainda pendente (destaque "etapa atual"). */
  isFirstPending(index: number): boolean {
    if (!this.d) return false;
    for (let i = 0; i < this.d.timeline.length; i++) {
      const s = this.d.timeline[i];
      if (!s.applies) continue;
      if (!s.datetime) return i === index;
    }
    return false;
  }

  /** Link do pedido no ERP Base (modelo em environment.urlErpBaseOrder, com {id}). */
  erpOrderUrl(baseOrderId?: string | null): string | null {
    const template = (environment as { urlErpBaseOrder?: string }).urlErpBaseOrder;
    if (!baseOrderId || !template) return null;
    return template.replace('{id}', encodeURIComponent(baseOrderId));
  }

  formatKey(key?: string | null): string {
    return (key || '').replace(/(.{4})/g, '$1 ').trim();
  }

  close(): void { this.dialogRef.close(this.changed); }

  trackByItem = (_: number, i: { idKitPaymentItem: number }) => i.idKitPaymentItem;
  trackByEvent = (i: number) => i;
}
